import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, User, Mail, Phone, MapPin, CreditCard, Lock, ArrowLeft, CheckCircle } from 'lucide-react';
import { registerTenant, getSubscriptionPlans } from '../../services/api/companyApi';
import LoadingOverlay from '../../components/common/LoadingOverlay';

const PublicCompanyRegistration = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1: Form, 2: Success
    const [loading, setLoading] = useState(false);
    const [registrationResult, setRegistrationResult] = useState(null);
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState('');
    const [plans, setPlans] = useState([]);

    useEffect(() => {
        getSubscriptionPlans()
            .then(res => {
                const PLAN_ORDER = { BASIC: 1, STANDARD: 2, PROFESSIONAL: 3, ENTERPRISE: 4 };
                const sorted = Object.entries(res.details || {})
                    .map(([key, d]) => ({ key, displayName: d.displayName, maxUsers: d.maxUsers, maxOrders: d.maxOrders, maxProducts: d.maxProducts }))
                    .sort((a, b) => (PLAN_ORDER[a.key] ?? 9) - (PLAN_ORDER[b.key] ?? 9));
                setPlans(sorted);
            })
            .catch(() => {
                // Fallback static options if API fails
                setPlans([
                    { key: 'BASIC',        displayName: 'Basic',        maxUsers: 5,  maxOrders: 500,   maxProducts: 100 },
                    { key: 'STANDARD',     displayName: 'Standard',     maxUsers: 20, maxOrders: 5000,  maxProducts: 400 },
                    { key: 'PROFESSIONAL', displayName: 'Professional', maxUsers: 50, maxOrders: 10000, maxProducts: 500 },
                    { key: 'ENTERPRISE',   displayName: 'Enterprise',   maxUsers: -1, maxOrders: -1,    maxProducts: -1  },
                ]);
            });
    }, []);

    const [formData, setFormData] = useState({
        // Company Details
        companyName: '',
        companyEmail: '',
        companyPhone: '',
        whatsappPhoneNumber: '',
        companyAddress: '',
        taxId: '',

        // Owner Details
        ownerFirstName: '',
        ownerLastName: '',
        ownerEmail: '',
        ownerUsername: '',
        ownerPassword: '',
        ownerConfirmPassword: '',
        ownerPhone: '',

        // Subscription
        subscriptionPlan: 'BASIC'
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Company validation
        if (!formData.companyName?.trim()) newErrors.companyName = 'Company name is required';
        if (!formData.companyEmail?.trim()) {
            newErrors.companyEmail = 'Company email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.companyEmail)) {
            newErrors.companyEmail = 'Invalid email format';
        }
        if (!formData.companyPhone?.trim()) newErrors.companyPhone = 'Company phone is required';

        if (!formData.whatsappPhoneNumber?.trim()) {
            newErrors.whatsappPhoneNumber = 'WhatsApp phone number is required';
        } else if (!/^\+?[1-9]\d{1,14}$/.test(formData.whatsappPhoneNumber)) {
            newErrors.whatsappPhoneNumber = 'Invalid format (e.g. +971501234567)';
        }

        // Owner validation
        if (!formData.ownerFirstName?.trim()) newErrors.ownerFirstName = 'First name is required';
        if (!formData.ownerLastName?.trim()) newErrors.ownerLastName = 'Last name is required';
        if (!formData.ownerEmail?.trim()) {
            newErrors.ownerEmail = 'Owner email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.ownerEmail)) {
            newErrors.ownerEmail = 'Invalid email format';
        }
        if (!formData.ownerUsername?.trim()) newErrors.ownerUsername = 'Username is required';
        if (!formData.ownerPassword?.trim()) {
            newErrors.ownerPassword = 'Password is required';
        } else if (formData.ownerPassword.length < 8) {
            newErrors.ownerPassword = 'Password must be at least 8 characters';
        }
        if (formData.ownerPassword !== formData.ownerConfirmPassword) {
            newErrors.ownerConfirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);
        setSubmitError('');
        try {
            const result = await registerTenant({
                // Company Details
                companyName: formData.companyName,
                companyEmail: formData.companyEmail,
                companyPhone: formData.companyPhone,
                whatsappPhoneNumber: formData.whatsappPhoneNumber,
                companyAddress: formData.companyAddress,
                taxId: formData.taxId,

                // Owner Details
                ownerFirstName: formData.ownerFirstName,
                ownerLastName: formData.ownerLastName,
                ownerEmail: formData.ownerEmail,
                ownerUsername: formData.ownerUsername,
                ownerPassword: formData.ownerPassword,
                ownerPhone: formData.ownerPhone,

                // Subscription
                subscriptionPlan: formData.subscriptionPlan
            });

            console.log('Registration successful:', result);
            setRegistrationResult(result);
            setStep(2);
        } catch (error) {
            console.error('Registration failed:', error);
            const serverMessage = error?.response?.data?.message
                || error?.response?.data?.error
                || error?.message
                || 'Registration failed. Please try again.';
            setSubmitError(serverMessage);
        } finally {
            setLoading(false);
        }
    };

    if (step === 2 && registrationResult) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle size={48} className="text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Successful!</h2>
                    <p className="text-gray-600 mb-6">Your company has been registered successfully.</p>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                        <h3 className="font-bold text-blue-900 mb-2">Your Login Credentials</h3>
                        <div className="space-y-2 text-sm">
                            <div>
                                <span className="text-gray-600">Username:</span>
                                <p className="font-mono font-bold text-gray-900">{formData.ownerUsername}</p>
                            </div>
                            <div>
                                <span className="text-gray-600">Email:</span>
                                <p className="font-mono font-bold text-gray-900">{formData.ownerEmail}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 text-left text-sm">
                        <p className="text-orange-800">
                            <strong>⚠️ Important:</strong> Please save your password securely. You'll need it to login.
                        </p>
                    </div>

                    <button
                        onClick={() => navigate('/login')}
                        className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold shadow-md hover:shadow-lg"
                    >
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
                    <button
                        onClick={() => navigate('/login')}
                        className="flex items-center gap-2 text-white hover:text-blue-100 transition-colors mb-2"
                    >
                        <ArrowLeft size={18} />
                        <span className="text-sm">Back to Login</span>
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                            <Building2 size={20} className="text-white" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-white">Register Your Company</h1>
                            <p className="text-blue-100 text-sm">Create your account and start managing your business</p>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto flex-1">
                    {/* Company Information */}
                    <div>
                        <h3 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
                            <Building2 size={18} className="text-blue-600" />
                            Company Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Company Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="companyName"
                                    value={formData.companyName}
                                    onChange={handleInputChange}
                                    className={`w-full px-3 py-2 border ${errors.companyName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm`}
                                    placeholder="Acme Corp"
                                />
                                {errors.companyName && <p className="text-red-500 text-xs mt-1">{errors.companyName}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Company Email <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    name="companyEmail"
                                    value={formData.companyEmail}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2.5 border ${errors.companyEmail ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                    placeholder="contact@acme.com"
                                />
                                {errors.companyEmail && <p className="text-red-500 text-xs mt-1">{errors.companyEmail}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Company Phone <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="tel"
                                    name="companyPhone"
                                    value={formData.companyPhone}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2.5 border ${errors.companyPhone ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                    placeholder="+971 50 123 4567"
                                />
                                {errors.companyPhone && <p className="text-red-500 text-xs mt-1">{errors.companyPhone}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    WhatsApp Number <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="tel"
                                    name="whatsappPhoneNumber"
                                    value={formData.whatsappPhoneNumber}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2.5 border ${errors.whatsappPhoneNumber ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                    placeholder="+971 50 123 4567"
                                />
                                {errors.whatsappPhoneNumber && <p className="text-red-500 text-xs mt-1">{errors.whatsappPhoneNumber}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Company Address</label>
                                <input
                                    type="text"
                                    name="companyAddress"
                                    value={formData.companyAddress}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    placeholder="Dubai, UAE"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Tax ID / Trade License</label>
                                <input
                                    type="text"
                                    name="taxId"
                                    value={formData.taxId}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    placeholder="TRN-123456"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Owner Information */}
                    <div>
                        <h3 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
                            <User size={18} className="text-purple-600" />
                            Owner Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    First Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="ownerFirstName"
                                    value={formData.ownerFirstName}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2.5 border ${errors.ownerFirstName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                    placeholder="John"
                                />
                                {errors.ownerFirstName && <p className="text-red-500 text-xs mt-1">{errors.ownerFirstName}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Last Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="ownerLastName"
                                    value={formData.ownerLastName}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2.5 border ${errors.ownerLastName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                    placeholder="Doe"
                                />
                                {errors.ownerLastName && <p className="text-red-500 text-xs mt-1">{errors.ownerLastName}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Email <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    name="ownerEmail"
                                    value={formData.ownerEmail}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2.5 border ${errors.ownerEmail ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                    placeholder="john@acme.com"
                                />
                                {errors.ownerEmail && <p className="text-red-500 text-xs mt-1">{errors.ownerEmail}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone</label>
                                <input
                                    type="tel"
                                    name="ownerPhone"
                                    value={formData.ownerPhone}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    placeholder="+971 50 123 4567"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Username <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="ownerUsername"
                                    value={formData.ownerUsername}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2.5 border ${errors.ownerUsername ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                    placeholder="johndoe"
                                />
                                {errors.ownerUsername && <p className="text-red-500 text-xs mt-1">{errors.ownerUsername}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Subscription Plan
                                </label>
                                <select
                                    name="subscriptionPlan"
                                    value={formData.subscriptionPlan}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                >
                                    {plans.map(plan => {
                                        const label = plan.maxUsers === -1
                                            ? `${plan.displayName} (Unlimited)`
                                            : `${plan.displayName} (${plan.maxUsers} users, ${plan.maxOrders.toLocaleString()} orders, ${plan.maxProducts.toLocaleString()} products)`;
                                        return (
                                            <option key={plan.key} value={plan.key}>{label}</option>
                                        );
                                    })}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Password <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="password"
                                    name="ownerPassword"
                                    value={formData.ownerPassword}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2.5 border ${errors.ownerPassword ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                    placeholder="Min. 8 characters"
                                />
                                {errors.ownerPassword && <p className="text-red-500 text-xs mt-1">{errors.ownerPassword}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Confirm Password <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="password"
                                    name="ownerConfirmPassword"
                                    value={formData.ownerConfirmPassword}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2.5 border ${errors.ownerConfirmPassword ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                    placeholder="Re-enter password"
                                />
                                {errors.ownerConfirmPassword && <p className="text-red-500 text-xs mt-1">{errors.ownerConfirmPassword}</p>}
                            </div>
                        </div>
                    </div>
                    {/* Submit error banner */}
                    {submitError && (
                        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-red-700 text-sm font-medium">{submitError}</p>
                        </div>
                    )}
                </form>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
                    <p className="text-xs text-gray-600">
                        Already have an account? <button onClick={() => navigate('/login')} className="text-blue-600 hover:text-blue-700 font-semibold">Login</button>
                    </p>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold shadow-md hover:shadow-lg disabled:opacity-50 text-sm"
                    >
                        Register Company
                    </button>
                </div>
            </div>

            {loading && (
                <LoadingOverlay
                    message="Registering Your Company..."
                    subMessage="Creating your account and setting up your workspace"
                />
            )}
        </div>
    );
};

export default PublicCompanyRegistration;
